import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LanguagePicker } from "@/components/layout/LanguagePicker";
import { HeaderAskBar } from "@/components/layout/HeaderAskBar";
import { BellIcon, ArrowRightStartOnRectangleIcon, Bars3Icon } from "@heroicons/react/24/outline";
import { useAuthStore } from "@/store/authStore";
import { useUIStore } from "@/store/uiStore";
import { useAlertStore } from "@/store/alertStore";
import { Badge } from "@/components/shared/Badge";
import { useUnacknowledgedAlertCount } from "@/api/alerts";
import { PublicSiteLink } from "@/components/shared/PublicSiteLink";
import { useBrandingStore } from "@/store/brandingStore";

export function AdminHeader() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const brand = useBrandingStore((st) => st.branding);
  const setMobileNavOpen = useUIStore((s) => s.setMobileNavOpen);
  const { data: alertCountData } = useUnacknowledgedAlertCount();
  const setUnacknowledgedCount = useAlertStore((s) => s.setUnacknowledgedCount);
  const alertCount = alertCountData?.count ?? 0;

  useEffect(() => {
    setUnacknowledgedCount(alertCount);
  }, [alertCount, setUnacknowledgedCount]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  const roleLabel = user?.role?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) ?? "";

  return (
    <header
      // h-16 rather than h-14 (MPPI-00151): the Ask bar now carries its guide
      // line inside its own box instead of absolutely positioning it below a
      // fixed-height header, where <main> covered it. The extra 8px is what
      // that line needs.
      // Header depth raised from h-16 to h-20 (+25%, operator 2026-08-29):
      // the bar read as compressed. The contents are scaled with it rather
      // than left floating in the extra space -- a taller bar around the
      // same small controls looks emptier, not more generous.
      className="flex h-20 items-center justify-between bg-primary px-4 md:px-6"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white md:hidden"
          aria-label="Open navigation"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
        <h2 className="text-lg text-white/80" style={{ fontFamily: "'Antic Didone', serif" }}>Admin Portal</h2>
      </div>

      {/* Ask, promoted out of the menu (MPPI-00092). */}
      <HeaderAskBar />
      <div className="flex items-center gap-4">
        {/* Language — MPPI-00012. Top right as asked, remembered per browser.
            Switching to Arabic flips the document to RTL; text without a
            verified translation stays in English rather than being guessed. */}
        <LanguagePicker />

        {/* Back to the tenant's own public site (MPPI-00419). */}
        <PublicSiteLink companyName={brand.company_name} website={brand.website} />

        {/* Notification bell */}
        <button
          onClick={() => navigate("/alerts")}
          className="relative rounded-md p-1.5 text-white/60 hover:bg-white/10 hover:text-white"
          aria-label="Notifications"
        >
          <BellIcon className="h-6 w-6" />
          {alertCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-primary">
              {alertCount > 99 ? "99+" : alertCount}
            </span>
          )}
        </button>

        {/* User info */}
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-base font-medium text-primary">
            {initials}
          </div>
          <div className="hidden text-[15px] md:block">
            <p className="font-medium text-white">{user?.name ?? "Unknown"}</p>
            <Badge variant="info">{roleLabel}</Badge>
          </div>
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="rounded-md p-1.5 text-white/60 hover:bg-white/10 hover:text-white"
          aria-label="Logout"
        >
          <ArrowRightStartOnRectangleIcon className="h-6 w-6" />
        </button>
      </div>
    </header>
  );
}
